import Button from "@builder/components/general/Button";
import OliverText from "@builder/components/general/OliverText";
import { addBuildGlobalColor, updateBuildGlobalColor } from "@builder/store/slices/BuilderSlice/builderSlice";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import { ClickAwayListener, Paper, Popper } from "@mui/material";
import { useEffect, useState } from "react";
import ColorInput, { GlobalColor } from "..";
import TextInput from "../../TextInput";
import "./style.scss";
import { updateBuildSettingsKey } from "@builder/store/slices/buildSettingsKeys/buildSettingsKeySlice";

interface GlobalColorModalProps {
    anchorEl: HTMLElement | null;
    type: "add" | "choose";
    setClose: () => void;
    currentHex?: string;
    setTheColor: (id: string) => void;
}

const AddGlobalColor = ({ hex, setJustAdded }: { hex: string; setJustAdded: (b: boolean) => void }) => {
    const [name, setName] = useState<string>("");
    const dispatch = useAppDispatch();

    const addColor = () => {
        dispatch(addBuildGlobalColor({ color: hex, name }));
        setJustAdded(true);
    };

    return (
        <div className="add-c">
            <OliverText text="name your color" size="xs" translatePrefix="sidebarCollapse." />
            <div className="new-color-name-input">
                <TextInput placeholder="Enter color name" value={name} callback={setName} />
                <Button buttonStyle="primary" text="Add" onClick={addColor} size="fit" />
            </div>
        </div>
    );
};

const ChooseGlobalColor = ({ onSelect, globalColors }: { onSelect: (id: string) => void; globalColors?: GlobalColor[] }) => {
    const dispatch = useAppDispatch();
    const buildSettingsKey = useAppSelector(state => state.buildSettingsKey);

    if (!globalColors || globalColors.length === 0) {
        return (
            <div className="choose-c">
                <div className="divider" />
                <OliverText text="No colors set yet" size="xs" color="base-grey" translatePrefix="sidebarCollapse." />
            </div>
        );
    }

    return (
        <div className="choose-c">
            <div className="divider" />
            {globalColors.map(color => (
                <div key={color.id} className="color-row">
                    <ColorInput
                        color={buildSettingsKey?.[color.color] ? buildSettingsKey?.[color.color].placeholder : color.color}
                        callback={value => {
                            if (buildSettingsKey?.[color.color]) {
                                const bsk = buildSettingsKey?.[color.color];
                                if (bsk.key && bsk.label) {
                                    dispatch(updateBuildSettingsKey({ key: bsk.key, update: { placeholder: value } }));
                                    dispatch(
                                        updateBuildGlobalColor({
                                            id: color.id,
                                            data: { name: bsk.label, color: bsk.key }
                                        })
                                    );
                                }
                            } else {
                                dispatch(
                                    updateBuildGlobalColor({
                                        id: color.id,
                                        data: { name: color.name, color: value }
                                    })
                                );
                            }
                        }}
                        options={{ disableGlobalColors: true, noText: true }}
                    />
                    <div className="choose" onClick={() => onSelect(color.id)}>
                        <OliverText text={color.name} size="xs" />
                    </div>
                </div>
            ))}
        </div>
    );
};

const GlobalColorModal = ({ anchorEl, setClose, type, currentHex, setTheColor }: GlobalColorModalProps) => {
    const [justAdded, setJustAdded] = useState<boolean>(false);
    const globalColors = useAppSelector(state => state.builder.build.extraAttributes?.buildGlobalColors);

    const handleGlobalColorSelection = (id: string) => {
        if (!globalColors) return;
        const color = globalColors.find(color => color.id === id);
        const cssVar = `var(--${color?.id})`;
        setTheColor(cssVar);
        setClose();
    };

    useEffect(() => {
        if (justAdded) {
            handleGlobalColorSelection(globalColors![globalColors!.length - 1].id);
            setJustAdded(false);
        }
    }, [justAdded]);

    return (
        <ClickAwayListener onClickAway={setClose}>
            <Popper open={true} anchorEl={anchorEl} placement="left-start" style={{ zIndex: 18 }}>
                <Paper className="global-color-modal" style={{ boxShadow: "none" }}>
                    <div className="header">
                        <OliverText text={type === "add" ? "Add to global colors" : "Global Colors"} size="l" translatePrefix="sidebarCollapse." />
                        <Button
                            buttonStyle="transparent"
                            icon={{
                                src: "./x-icon-dark.svg"
                            }}
                            style={{ padding: "0", filter: "var(--theme-filter-svg)" }}
                            onClick={setClose}
                        />
                    </div>
                    {type === "add" && currentHex && <AddGlobalColor hex={currentHex} setJustAdded={setJustAdded} />}
                    {type === "choose" && <ChooseGlobalColor onSelect={handleGlobalColorSelection} globalColors={globalColors} />}
                </Paper>
            </Popper>
        </ClickAwayListener>
    );
};

export default GlobalColorModal;
